import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  Box, Button, Flex, Grid, GridItem, Image, Text,
  VStack, HStack, Badge, Alert, Stack, Radio, Checkbox,
  Textarea, Spacer, Heading, useColorModeValue, Select,
} from "@chakra-ui/react";
import { FaFlag, FaClock } from "react-icons/fa";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { AttemptedQuestionDetails, Choice } from "../Common/types";
import { RenderQuestionContext, RenderTextWithImages, QuestionContext } from "../../utils/questionRendering";

export interface RendererStats {
  answeredWithFlag: number; answeredWithoutFlag: number;
  visitedWithFlag: number; visitedWithoutFlag: number;
  notVisited: number; total: number;
}

export interface RendererHandle {
  getStats: () => RendererStats;
}

interface QuestionSetViewAttemptProps {
  examAttemptedQuestionSetProp: AttemptedQuestionDetails[];
}

const EVAL_STATUS_COLOR: Record<string, string> = {
  CORRECT: "green.400",
  INCORRECT: "red.400",
  PARTIALLY_CORRECT: "yellow.400",
  MANUALLY_EVALUATED: "cyan.400",
};

const formatTimeSpent = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

const LegendItem = ({ color, label, flag = false, border = false }: {
  color: string; label: string; flag?: boolean; border?: boolean;
}) => (
  <Flex align="center" gap={1}>
    <Box position="relative" w="14px" h="14px">
      <Box w="100%" h="100%" borderRadius="full" bg={color}
        border={border ? '2px solid' : 'none'}
        borderColor={border ? color : 'transparent'} />
      {flag && (
        <Box position="absolute" top="-4px" right="-4px" color="purple.500" fontSize="2xs">
          <FaFlag />
        </Box>
      )}
    </Box>
    <Text fontSize="xs" whiteSpace="nowrap">{label}</Text>
  </Flex>
);

type ClozeSegment = { type: "text"; content: string } | { type: "dropdown"; id: string };

function parseClozeText(text: string): ClozeSegment[] {
  const segments: ClozeSegment[] = [];
  const regex = /\{\{([DG]\d+)\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex)
      segments.push({ type: "text", content: text.slice(lastIndex, match.index) });
    segments.push({ type: "dropdown", id: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length)
    segments.push({ type: "text", content: text.slice(lastIndex) });
  return segments;
}

const QuestionSetViewAttempt = forwardRef<RendererHandle, QuestionSetViewAttemptProps>(
  ({ examAttemptedQuestionSetProp }, ref) => {
    const [examAttemptedQuestionSet] = useState<AttemptedQuestionDetails[]>(examAttemptedQuestionSetProp);
    const [currentIndex, setCurrentIndex] = useState(0);

    const questionPaneBg = useColorModeValue("gray.50", "gray.700");
    const cardBg = useColorModeValue("white", "gray.800");

    const contextPaneBg = useColorModeValue("blue.50", "gray.800");

    const hasValidContext = (ctx: any): ctx is QuestionContext =>
      Boolean(ctx) && typeof ctx === 'object' && 'type' in ctx;

    useImperativeHandle(ref, () => ({
      getStats: () => {
        const total = examAttemptedQuestionSet.length;
        const answeredWithFlag    = examAttemptedQuestionSet.filter(q => q.status === "ANSWERED" && q.flagged).length;
        const answeredWithoutFlag = examAttemptedQuestionSet.filter(q => q.status === "ANSWERED" && !q.flagged).length;
        const visitedWithFlag     = examAttemptedQuestionSet.filter(q => q.status === "VISITED"  && q.flagged).length;
        const visitedWithoutFlag  = examAttemptedQuestionSet.filter(q => q.status === "VISITED"  && !q.flagged).length;
        const notVisited          = examAttemptedQuestionSet.filter(q => q.status === "NOT_VISITED").length;
        return { total, answeredWithFlag, answeredWithoutFlag, visitedWithFlag, visitedWithoutFlag, notVisited };
      }
    }));

    function replaceReferencesWithLabels(text: string, choiceIdToLabel: Record<number, string>): string {
      return text.replace(/@\{(\d+)\}/g, (_, id) => {
        const choiceId = parseInt(id);
        return choiceIdToLabel[choiceId] || `@{${id}}`;
      });
    }

    function isNumberArray(arr: unknown): arr is number[] {
      return Array.isArray(arr) && typeof arr[0] === 'number';
    }

    const sortedQuestions = [...examAttemptedQuestionSet].sort((a, b) => a.sequence_no - b.sequence_no);
    const question = sortedQuestions[currentIndex];

    const choiceIdToLabel: Record<number, string> = {};
    (question.options_display_order ?? []).forEach((id, idx) => {
      choiceIdToLabel[id] = String.fromCharCode(65 + idx);
    });

    const goToIndex = (idx: number) => {
      if (idx >= 0 && idx < sortedQuestions.length) setCurrentIndex(idx);
    };

    return (
      <Flex direction="column" h="100%">

        {/* Question Review Map */}
        <Box bg={cardBg} p={4} borderRadius="md" borderWidth="1px" mb={4} maxH="25vh" overflowY="auto">
          <Heading size="sm" mb={3}>Question Review Map</Heading>
          <Grid templateColumns="repeat(auto-fill, minmax(48px, 1fr))" gap={3}>
            {sortedQuestions.map((q, idx) => {
              const statusColor = EVAL_STATUS_COLOR[q.evaluation_status] || "gray.400";
              const isCurrent = idx === currentIndex;
              return (
                <GridItem key={q.question_id}>
                  <Flex direction="column" align="center" position="relative" role="button"
                    _hover={{ transform: 'scale(1.08)', cursor: 'pointer' }}
                    transition="transform 0.2s" onClick={() => goToIndex(idx)}>
                    <Box position="relative">
                      <Box w="36px" h="36px" borderRadius="full" bg={statusColor}
                        border={isCurrent ? '3px solid' : 'none'}
                        borderColor={isCurrent ? 'blue.500' : 'transparent'}
                        display="flex" alignItems="center" justifyContent="center"
                        fontWeight="bold" fontSize="sm" color="white" boxShadow="md">
                        {q.sequence_no}
                      </Box>
                      {q.flagged && (
                        <Box position="absolute" top="-6px" right="-8px" fontSize="sm" color="purple.500">
                          <FaFlag />
                        </Box>
                      )}
                    </Box>
                  </Flex>
                </GridItem>
              );
            })}
          </Grid>
          <HStack mt={3} spacing={4} wrap="wrap" fontSize="xs">
            <LegendItem color="green.400" label="Correct" />
            <LegendItem color="red.400" label="Incorrect" />
            <LegendItem color="yellow.400" label="Partially Correct" />
            <LegendItem color="cyan.400" label="Manually Evaluated" />
            <LegendItem color="gray.400" label="Flagged" flag />
            <LegendItem color="blue.500" label="Current Question" border />
          </HStack>
        </Box>

        {/* Single Question Detail */}
        <Box flex="1" overflowY="auto" p={4}>
          <Box p={4} borderWidth="1px" shadow="lg" borderRadius="md" bg={questionPaneBg}>

{/*             <Flex align="center" mb={2}>
              <Spacer />
              <HStack spacing={2}>
                {typeof question.time_spent === 'number' && (
                  <Badge colorScheme="blue" display="flex" alignItems="center" gap={1} px={2}>
                    <FaClock size={10} />
                    {formatTimeSpent(question.time_spent)}
                  </Badge>
                )}
                {question.flagged && <Badge colorScheme="purple">Flagged</Badge>}
              </HStack>
            </Flex> */}

            {/* Context + question split, or full-width */}
            {/* ── shared question body -- called in both split and full-width layouts ── */}
            {(() => {
              const renderQuestionBody = () => (
                <>
                  <Flex align="center" mb={2}>
                    <Spacer />
                    <HStack spacing={2}>
                      {typeof question.time_spent === 'number' && (
                        <Badge colorScheme="blue" display="flex" alignItems="center" gap={1} px={2}>
                          <FaClock size={10} />
                          {formatTimeSpent(question.time_spent)}
                        </Badge>
                      )}
                      {question.flagged && <Badge colorScheme="purple">Flagged</Badge>}
                    </HStack>
                  </Flex>

                  <Text fontSize="sm" fontWeight="semibold" mb={1}>
                    Q{question.sequence_no}:
                  </Text>
                  {question.question_type !='CLOZE_DROPDOWN' &&  question.question_type != 'CLOZE_GAP_FILL'  && (
                  
                  <RenderTextWithImages
                    text={question.question_text ?? ""}
                    fontSize="sm"
                    fontWeight="semibold"
                  />

                  )}

                  <Alert status="info" fontSize="sm" my={3}>
                    {(() => {
                      const qt = question.question_type;
                      if (qt === "MCQ_SINGLE") return "Single choice question.";
                      if (qt === "MCQ_MULTI")
                        return question.max_selections
                          ? `Up to ${question.max_selections} options could be selected.`
                          : "Multiple options could be selected.";
                      if (qt === "CLOZE_DROPDOWN") return "Select the best word for each gap.";
                      if (qt === "CLOZE_GAP_FILL") return "Select the sentence that best completes each gap.";
                      if (qt === "SHORT_ANSWER") return "Short answer question.";
                      if (qt === "ESSAY_TEXT") return "Extended response question.";
                      if (qt === "WRITTEN_SUBMISSION") return "Written submission — answer provided on paper.";
                      return "";
                    })()}
                  </Alert>

                  {/* MCQ */}
                  {(question.question_type === "MCQ_SINGLE" || question.question_type === "MCQ_MULTI") && (() => {
                    const choices = question.choices ?? [];
                    const hasDisplayOrder =
                      question.options_display_order && question.options_display_order.length > 0;
                    const orderedChoices = hasDisplayOrder
                      ? (question.options_display_order ?? [])
                          .map(id => choices.find(c => c.id === id))
                          .filter((c): c is Choice => !!c)
                      : choices;

                    const choiceIdToLabel: Record<number, string> = {};
                    orderedChoices.forEach((choice, idx) => {
                      choiceIdToLabel[choice.id] = String.fromCharCode(65 + idx);
                    });

                    return (
                      <Stack spacing={3}>
                        {orderedChoices.map((choice, idx) => {
                          const isSelected = question.selected_option_ids?.includes(choice.id);
                          let isCorrect = false;
                          if (isNumberArray(question.correct_answer)) {
                            if (hasDisplayOrder) {
                              const displayIndex = (question.options_display_order ?? [])
                                .findIndex(id => id === choice.id);
                              isCorrect = displayIndex !== -1 &&
                                question.correct_answer.includes(displayIndex + 1);
                            } else {
                              isCorrect = question.correct_answer.includes(idx + 1);
                            }
                          }
                          return (
                            <Box key={choice.id} p={2} border="1px solid"
                              borderColor={isCorrect ? "green.400" : isSelected ? "blue.300" : "gray.300"}
                              borderRadius="md"
                              bg={isCorrect ? "green.50" : isSelected ? "blue.50" : "white"}>
                              <HStack>
                                {question.question_type === "MCQ_SINGLE"
                                  ? <Radio isChecked={!!isSelected} isDisabled />
                                  : <Checkbox isChecked={!!isSelected} isDisabled />}
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="medium" fontSize="sm" color="gray.700">
                                    {choiceIdToLabel[choice.id]}. {replaceReferencesWithLabels(choice.text ?? "", choiceIdToLabel)}
                                  </Text>
                                  {choice.image && (
                                    <Image src={choice.image} alt="Option"
                                      maxW="100%" maxH="100px" borderRadius="md" />
                                  )}
                                </VStack>
                              </HStack>
                            </Box>
                          );
                        })}
                      </Stack>
                    );
                  })()}

                  {/* CLOZE_DROPDOWN */}
                  {question.question_type === "CLOZE_DROPDOWN" && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" whiteSpace="pre-line" mb={3} lineHeight="2.4">
                        {parseClozeText(question.question_text ?? "").map((seg, idx) => {
                          if (seg.type === "text") {
                            return <React.Fragment key={idx}>{seg.content}</React.Fragment>;
                          }
                          const dropdownId = parseInt(seg.id.slice(1));
                          const group = (question.choices as any[])?.find(g => g.dropdown_id === dropdownId);
                          const options: { id: number; text: string }[] = group?.options ?? [];
                          const studentCloze = (question.selected_cloze_answers ?? {}) as Record<string, any>;
                          const currentValue = studentCloze[seg.id];
                          const selectedId = Array.isArray(currentValue) ? currentValue[0] : currentValue;
                          const correctAnswerDict = (question.correct_answer ?? {}) as Record<string, any>;
                          const correctRaw = correctAnswerDict[seg.id] ?? correctAnswerDict[String(dropdownId)];
                          const correctIds: number[] = Array.isArray(correctRaw) ? correctRaw : (correctRaw != null ? [correctRaw] : []);
                          const correctOption = options.find(o => correctIds.includes(o.id));
                          const isCorrect = selectedId != null && correctIds.includes(Number(selectedId));
                          const isAnswered = selectedId != null;
                          return (
                            <Box key={idx} display="inline-block" mx={1} verticalAlign="middle">
                              <Select size="sm" w="auto" minW="140px" maxW="260px"
                                value={selectedId ?? ""} isDisabled
                                borderColor={!isAnswered ? "gray.300" : isCorrect ? "green.400" : "red.400"}
                                bg={!isAnswered ? "white" : isCorrect ? "green.50" : "red.50"}>
                                {options.map(opt => (
                                  <option key={opt.id} value={opt.id}>{opt.text}</option>
                                ))}
                                {!isAnswered && <option value="">Not answered</option>}
                              </Select>
                              {!isCorrect && correctOption && (
                                <Text fontSize="2xs" color="green.600" mt={0.5}>✓ {correctOption.text}</Text>
                              )}
                            </Box>
                          );
                        })}
                      </Text>
                    </Box>
                  )}

                  {/* CLOZE_GAP_FILL */}
                  {question.question_type === "CLOZE_GAP_FILL" && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" whiteSpace="pre-line" mb={3} lineHeight="2.4">
                        {parseClozeText(question.question_text ?? "").map((seg, idx) => {
                          if (seg.type === "text") {
                            return <React.Fragment key={idx}>{seg.content}</React.Fragment>;
                          }
                          const pool: { id: number; text: string }[] =
                            (question.choices as any)?.option_pool ?? [];
                          const studentCloze = (question.selected_cloze_answers ?? {}) as Record<string, number>;
                          const selectedId = studentCloze[seg.id];
                          const gapNum = seg.id.slice(1);
                          const correctAnswerDict = (question.correct_answer ?? {}) as Record<string, number>;
                          const correctId = correctAnswerDict[seg.id] ?? correctAnswerDict[gapNum];
                          const correctOption = pool.find(o => o.id === correctId);
                          const isCorrect = selectedId != null && selectedId === correctId;
                          const isAnswered = selectedId != null;
                          return (
                            <Box key={idx} display="inline-block" mx={1} verticalAlign="middle">
                              <Select size="sm" w="auto" minW="160px" maxW="300px"
                                value={selectedId ?? ""} isDisabled
                                borderColor={!isAnswered ? "gray.300" : isCorrect ? "green.400" : "red.400"}
                                bg={!isAnswered ? "white" : isCorrect ? "green.50" : "red.50"}>
                                {pool.map(opt => (
                                  <option key={opt.id} value={opt.id}>{opt.text}</option>
                                ))}
                                {!isAnswered && <option value="">Not answered</option>}
                              </Select>
                              {!isCorrect && correctOption && (
                                <Text fontSize="2xs" color="green.600" mt={0.5}>✓ {correctOption.text}</Text>
                              )}
                            </Box>
                          );
                        })}
                      </Text>
                    </Box>
                  )}

                  {/* SHORT_ANSWER */}
                  {question.question_type === "SHORT_ANSWER" && (
                    <Box>
                      <Textarea value={question.text_response ?? ""} isDisabled resize="vertical" rows={3} fontSize="sm" />
                      {Array.isArray(question.correct_answer) && !isNumberArray(question.correct_answer) && (
                        <Box mt={2} p={2} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.200">
                          <Text fontSize="xs" color="green.700" fontWeight="semibold">Accepted answers:</Text>
                          {(question.correct_answer as string[]).map((ans, i) => (
                            <Text key={i} fontSize="sm" color="green.700">• {ans}</Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* ESSAY_TEXT */}
                  {question.question_type === "ESSAY_TEXT" && (
                    <Textarea value={question.text_response ?? ""} isDisabled resize="vertical" rows={8} fontSize="sm" />
                  )}

                  {/* WRITTEN_SUBMISSION */}
                  {question.question_type === "WRITTEN_SUBMISSION" && (
                    <Box p={4} border="1px dashed" borderColor="gray.300" borderRadius="md" bg="gray.50">
                      <Text fontSize="sm" color="gray.500">
                        This question was answered on a physical answer sheet submitted to the invigilator.
                      </Text>
                    </Box>
                  )}
                </>
              );

              // ── Layout: split when context present, full-width when absent ──
              return hasValidContext(question?.question_context) ? (
                <Flex gap={4} align="stretch" mb={4}>
                  <Box w="45%" p={3} border="1px solid" borderColor="blue.200"
                    borderRadius="md" bg={contextPaneBg} overflowY="auto" maxH="500px">
                    <Text fontSize="xs" fontWeight="bold" color="blue.500" mb={2}>CONTEXT</Text>
                    <RenderQuestionContext context={question.question_context} />
                  </Box>
                  <Box w="55%" overflowY="auto">
                    {renderQuestionBody()}
                  </Box>
                </Flex>
              ) : (
                <Box mb={3}>
                  {renderQuestionBody()}
                </Box>
              );
            })()}
            {/* Evaluation summary */}
            <Box mt={4} fontSize="sm">
              <HStack spacing={4} mb={1}>
                <Text>
                  Evaluation Status:{" "}
                  <Badge colorScheme={
                    question.evaluation_status === "CORRECT" ? "green" :
                    question.evaluation_status === "INCORRECT" ? "red" :
                    question.evaluation_status === "PARTIALLY_CORRECT" ? "yellow" :
                    question.evaluation_status === "MANUALLY_EVALUATED" ? "teal" : "gray"
                  }>{question.evaluation_status}</Badge>
                </Text>
                {typeof question.time_spent === 'number' && (
                  <Text color="gray.500">Time Spent: <strong>{formatTimeSpent(question.time_spent)}</strong></Text>
                )}
              </HStack>
              {typeof question.awarded_marks === 'number' && (
                <Text>Awarded Marks: {question.awarded_marks.toFixed(2)}</Text>
              )}
              {typeof question.max_marks === 'number' && (
                <Text>Maximum Marks: {question.max_marks}</Text>
              )}
              {question.explanation && question.explanation.trim() !== "" && (
                <Box mt={2} p={2} borderWidth="1px" borderStyle="dashed" borderColor="gray.500" borderRadius="md">
                  <Text fontWeight="medium" color="gray.400">Explanation:</Text>
                  <Text whiteSpace="pre-line">{question.explanation}</Text>
                </Box>
              )}
              {question.tutor_remarks && question.tutor_remarks.trim() !== "" && (
                <Box mt={2} p={2} borderWidth="1px" borderStyle="dashed" borderColor="gray.500" borderRadius="md">
                  <Text fontWeight="medium" color="gray.400">Tutor Remarks:</Text>
                  <Text whiteSpace="pre-line">{question.tutor_remarks}</Text>
                </Box>
              )}
              <Text>
                Correct Answer:{" "}
                {isNumberArray(question.correct_answer)
                  ? question.correct_answer
                      .map((ansIndex) => {
                        const labelIndex = ansIndex - 1;
                        return labelIndex >= 0 && labelIndex < (question.options_display_order ?? []).length
                          ? String.fromCharCode(65 + labelIndex) : "?";
                      })
                      .reverse()
                      .join(", ")
                  : Array.isArray(question.correct_answer)
                    ? question.correct_answer.join(", ")
                    : String(question.correct_answer)}
              </Text>
            </Box>
          </Box>

          {/* Prev / Next */}
          <Flex justify="space-between" mt={4}>
            <Button leftIcon={<ChevronLeftIcon />} onClick={() => goToIndex(currentIndex - 1)}
              isDisabled={currentIndex === 0} variant="outline">
              Previous
            </Button>
            <Text fontSize="sm" color="gray.500" alignSelf="center">
              Question {currentIndex + 1} of {sortedQuestions.length}
            </Text>
            <Button rightIcon={<ChevronRightIcon />} onClick={() => goToIndex(currentIndex + 1)}
              isDisabled={currentIndex === sortedQuestions.length - 1} variant="outline">
              Next
            </Button>
          </Flex>
        </Box>
      </Flex>
    );
  }
);

export default QuestionSetViewAttempt;